/**
 * Reusable "pick one editable field + one value" control.
 *
 * Extracted from <BulkEditDialog> (BulkActions.tsx) so the exact same picker
 * powers both the selection bulk-edit dialog and the file-driven «Обновление из
 * файла» page. It renders:
 *   1. a <Select> of the editable fields (EDITABLE_FIELD_DEFS, catalog order), and
 *   2. the shared <ValueEditor> for the chosen field's value.
 *
 * The value is kept as a STRING draft (comma-joined for multiselect), exactly like
 * the filter builder. The component is fully controlled: the parent owns both the
 * selected field key and the draft string. On a field change the parent should
 * reset its draft (see BulkEditDialog.onFieldChange) — the picker only reports the
 * new key. Use the exported `buildValue(def, draft)` to turn the draft into the
 * wire value {field: value} the endpoint expects.
 */

import { useRefs } from "@/api/hooks";
import {
  EDITABLE_FIELD_DEFS,
  FIELD_BY_KEY,
  resolveOptions,
  type FieldDef,
} from "@/lib/fields";
import { ValueEditor } from "@/components/ValueEditor";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** Field keys whose values mob expects as integer arrays ([int], not ["int"]). */
export const NUMERIC_ID_FIELDS: ReadonlySet<string> = new Set([
  "target_group_ids",
  "citizen_category_ids",
]);

/** Operator to feed the shared ValueEditor for a field kind (no filter-operator UI). */
export function editorOperator(def: FieldDef): "eq" | "among" {
  // multiselect -> 'among' so ValueEditor renders the chip multi-select and we collect
  // an array; everything else -> 'eq' renders the single picker.
  return def.kind === "multiselect" ? "among" : "eq";
}

/**
 * Serialize the draft string from ValueEditor into the wire value for `field`.
 * Mirrors FilterBuilder.toClause but without operator/valueless handling:
 *   - bool        -> 'y' | 'n'
 *   - multiselect -> string[]  (or number[] for *_ids)
 *   - select/text -> the string as-is
 * Returns null when nothing usable was entered (empty / no chips).
 */
export function buildValue(
  def: FieldDef,
  draft: string,
): string | number[] | string[] | null {
  if (def.kind === "bool") {
    // TODO(F-bool, ревью 2026-07-07): bool-поля (is_party_loyal / is_president_loyal /
    // is_high_authority_loyal / verified) отключены для МАССОВОЙ ЗАПИСИ через
    // bulkWritable:false в lib/fields.ts, поэтому сюда bool-def сейчас не доходит.
    // Формат записи не подтверждён: реверс проекта фиксирует is_*_loyal -> bool,
    // verified -> int 0/1, а 'y'/'n' задокументирован ТОЛЬКО для GET-фильтров.
    // После живой пробы на одном id привести это значение и normalize_values (бэк)
    // к подтверждённому формату и снять bulkWritable:false, чтобы вернуть поля.
    return draft === "n" ? "n" : "y";
  }
  if (def.kind === "multiselect") {
    // INVARIANT (Xfe-2): the draft is comma-joined, so multiselect OPTION VALUES must
    // be comma-free. Today they are — target_group_ids/citizen_category_ids are numeric
    // ids, status is short refs codes — so the round-trip is lossless. Any future
    // multiselect whose option values can contain a comma must switch this delimiter.
    const parts = draft
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v !== "");
    if (parts.length === 0) return null;
    if (NUMERIC_ID_FIELDS.has(def.field)) {
      const nums = parts.map(Number).filter((n) => Number.isFinite(n));
      return nums.length > 0 ? nums : null;
    }
    return parts;
  }
  // select / readiness / readiness_region / text-ish
  const trimmed = draft.trim();
  return trimmed === "" ? null : trimmed;
}

export interface FieldValuePickerProps {
  /** Currently selected editable field key (must live in EDITABLE_FIELD_DEFS). */
  fieldKey: string;
  /** Current draft value string (comma-joined for multiselect). */
  draftValue: string;
  /** Called with the newly picked field key. Parent should reset its draft. */
  onFieldChange: (key: string) => void;
  /** Called with the new draft string as the user edits the value. */
  onValueChange: (draft: string) => void;
  /** data-testid for the field <Select> trigger (kept stable for existing tests). */
  fieldTestId?: string;
}

export function FieldValuePicker({
  fieldKey,
  draftValue,
  onFieldChange,
  onValueChange,
  fieldTestId = "bulk-edit-field",
}: FieldValuePickerProps) {
  const { data: refs } = useRefs();
  const def = FIELD_BY_KEY[fieldKey];
  const options = def ? resolveOptions(def, refs) : [];

  return (
    <>
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Поле</Label>
        <Select value={fieldKey} onValueChange={onFieldChange}>
          <SelectTrigger className="h-8" data-testid={fieldTestId}>
            <SelectValue placeholder="Поле" />
          </SelectTrigger>
          <SelectContent>
            {EDITABLE_FIELD_DEFS.map((f) => (
              <SelectItem key={f.field} value={f.field}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Новое значение</Label>
        <ValueEditor
          def={def}
          operator={def ? editorOperator(def) : "eq"}
          value={draftValue}
          options={options}
          cascadeBranchId={null}
          onChange={onValueChange}
        />
      </div>
    </>
  );
}
